import { useTriggerPandaDocSync, useTriggerSmartBillSync } from '@actions';
import Button from '@components/V4/Button';
import { SmartBillSyncStatusDTO } from '@shared/clientTypes/DTOs/ContractsDTO';
import { SmartBillSyncStatus } from '@shared/enums';
import useToast from 'apps/agora/src/hooks/useToast';
import { mergeClassNames } from 'apps/agora/src/utils/helpers';
import moment from 'moment';

interface SyncStatusBarProps {
  status?: SmartBillSyncStatusDTO;
}

const SyncStatusBar = (props: SyncStatusBarProps) => {
  const { status } = props;
  const toast = useToast();

  const { mutate: triggerSync, isLoading: isTriggering } = useTriggerSmartBillSync({
    onSuccess: () => toast.success('SmartBill sync started. Results will appear when it finishes.'),
    onError: (error: any) =>
      toast.error(error?.response?.data?.error?.message ?? 'Could not start the SmartBill sync.'),
  });

  const { mutate: triggerPandaDocSync, isLoading: isImportingDocuments } = useTriggerPandaDocSync({
    onSuccess: () =>
      toast.success('PandaDoc import started. Contracts will appear when it finishes.'),
    onError: (error: any) =>
      toast.error(error?.response?.data?.error?.message ?? 'Could not start the PandaDoc import.'),
  });

  const lastRun = status?.lastRun;
  const lastRunTone =
    lastRun?.status === SmartBillSyncStatus.Failed
      ? 'text-customRed'
      : lastRun?.status === SmartBillSyncStatus.Running
      ? 'text-customGold'
      : 'text-customGreen';

  return (
    <div className="flex flex-wrap items-center gap-3 rounded-2xl bg-surfaceObject px-4 py-3 text-xs">
      <span
        className={mergeClassNames(
          'h-2 w-2 shrink-0 rounded-full',
          !status?.configured
            ? 'bg-customGrey'
            : status.running
            ? 'animate-pulse bg-customGold'
            : lastRun?.status === SmartBillSyncStatus.Failed
            ? 'bg-customRed'
            : 'bg-customGreen'
        )}
      />
      <div className="flex flex-col gap-0.5">
        <span className="font-semibold text-white">
          SmartBill{' '}
          {!status?.configured
            ? '· not configured on this environment'
            : status.running
            ? '· sync in progress'
            : status.syncEnabled
            ? '· daily sync enabled'
            : '· daily sync disabled (manual only)'}
        </span>
        {lastRun ? (
          <span className="text-customGrey">
            Last run {moment(lastRun.startedAt).fromNow()} ·{' '}
            <span className={lastRunTone}>{lastRun.status}</span> ·{' '}
            {lastRun.stats.invoicesDiscovered} discovered, {lastRun.stats.invoicesRefreshed}{' '}
            refreshed, {lastRun.stats.invoicesAutoLinked} auto-linked, {lastRun.stats.apiCalls} API
            calls
            {lastRun.stats.errors.length
              ? ` · ${lastRun.stats.errors.length} warning${
                  lastRun.stats.errors.length === 1 ? '' : 's'
                }`
              : ''}
          </span>
        ) : (
          <span className="text-customGrey">No sync has run yet.</span>
        )}
        {lastRun?.stats.errors.length ? (
          <ul className="mt-1 list-disc pl-4 text-customGrey">
            {lastRun.stats.errors.slice(0, 3).map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        ) : null}
      </div>
      <div className="ml-auto flex items-center gap-2">
        {status?.unmappedInvoices ? (
          <span className="rounded-full bg-customGold/10 px-2 py-0.5 font-semibold text-customGold">
            {status.unmappedInvoices} unmapped invoice{status.unmappedInvoices === 1 ? '' : 's'}
          </span>
        ) : null}
        <Button
          variant="secondary"
          buttonText="Import PandaDoc"
          isLoading={isImportingDocuments}
          onClick={() => triggerPandaDocSync({})}
        />
        <Button
          variant="secondary"
          buttonText="Sync now"
          disabled={!status?.configured || status.running}
          isLoading={isTriggering}
          onClick={() => triggerSync(undefined)}
        />
      </div>
    </div>
  );
};

export default SyncStatusBar;
